import { orgAPI } from "@/api/api";
import { useEventStreamUrl } from "@/api/inbox";
import { useHandleWebSocketEvent } from "@/hooks/useHandleWebSocketEvent";
import Sentry from "@/integrations/Sentry";
import { EventStreamEvent, EventStreamHandler } from "@/types/events";
import { createEventStreamWebSocket } from "@cohesive/api-client";
import createDebug from "debug";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDebounceCallback } from "usehooks-ts";

const debug = createDebug("cohesive:event-stream");

// App-level heartbeat: browsers can't observe protocol ping/pong, so a
// half-open socket can sit in readyState OPEN with no events. Send a ping
// and force a reconnect if the server stays silent past the timeout.
const HEARTBEAT_INTERVAL_MS = 15_000;
const SERVER_SILENCE_TIMEOUT_MS = 45_000;

interface EventStreamContextType {
  registerHandler: (handler: EventStreamHandler) => () => void;
  isConnected: boolean;
}

const EventStreamContext = createContext<EventStreamContextType | null>(null);

interface ProviderProps {
  children: React.ReactNode;
}

export const EventStreamProvider = React.memo(({ children }: ProviderProps) => {
  const socketRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const handlersRef = useRef<Set<EventStreamHandler>>(new Set());
  const isReceivingMessagesRef = useRef<boolean>(false);
  const activeStreamingMessageIdsRef = useRef<Set<string>>(new Set());
  const disconnectCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastServerMessageAtRef = useRef<number>(0);

  const { data: eventStreamUrl } = useEventStreamUrl();
  const handleWebSocketEvent = useHandleWebSocketEvent();

  const syncReceivingMessagesState = () => {
    isReceivingMessagesRef.current =
      activeStreamingMessageIdsRef.current.size > 0;
  };

  const markMessageAsStreaming = (messageId: string) => {
    activeStreamingMessageIdsRef.current.add(messageId);
    syncReceivingMessagesState();
  };

  const markMessageAsTerminal = (messageId: string) => {
    activeStreamingMessageIdsRef.current.delete(messageId);
    syncReceivingMessagesState();
  };

  const clearStreamingMessages = () => {
    activeStreamingMessageIdsRef.current.clear();
    syncReceivingMessagesState();
  };

  const connectWebSocket = useDebounceCallback(
    async () => {
      if (!eventStreamUrl) return;

      debug("EventStream WebSocket connecting to: %s", eventStreamUrl);

      if (
        socketRef.current?.readyState === WebSocket.OPEN ||
        socketRef.current?.readyState === WebSocket.CONNECTING ||
        socketRef.current?.readyState === WebSocket.CLOSING
      ) {
        debug("EventStream WebSocket already connected or connecting");
        return;
      }

      try {
        if (socketRef.current) {
          debug("EventStream WebSocket closing");
          socketRef.current.close();
          socketRef.current = null;
        }

        const accessToken = await orgAPI.getToken();
        const socket = createEventStreamWebSocket(eventStreamUrl, accessToken);
        socketRef.current = socket;

        // A superseded socket (token refresh / reconnect closes the old one
        // and opens a new one) must not clobber the live connection's state.
        const isCurrentSocket = () => socketRef.current === socket;

        socket.onopen = (ev) => {
          if (!isCurrentSocket()) return;
          debug("EventStream WebSocket connected: %o", ev);
          clearStreamingMessages();
          setIsConnected(true);
          startHeartbeatInterval();
        };

        socket.onmessage = (event) => {
          if (!isCurrentSocket()) return;
          lastServerMessageAtRef.current = Date.now();

          const data: EventStreamEvent = JSON.parse(event.data);

          // Heartbeat pong: liveness signal only, not a real event.
          if ((data as { type: string }).type === "pong") {
            return;
          }

          // Track if we're receiving streaming messages
          if (data.type === "flow_message.content_delta") {
            const contentDelta = data.data.flow_message_content_delta;
            if (contentDelta.done) {
              markMessageAsTerminal(contentDelta.flow_message_id);
            } else {
              markMessageAsStreaming(contentDelta.flow_message_id);
            }
          } else if (data.type === "flow_message.thinking_delta") {
            // Thinking completion can precede content streaming, so keep this
            // message marked as active until a terminal update/content done.
            markMessageAsStreaming(
              data.data.flow_message_thinking_delta.flow_message_id,
            );
          } else if (data.type === "flow_message.tool_call_args_delta") {
            markMessageAsStreaming(
              data.data.flow_message_tool_call_args_delta.flow_message_id,
            );
          } else if (data.type === "flow_message.updated") {
            const { flow_message: flowMessage } = data.data;
            if (
              flowMessage.status === "done" ||
              flowMessage.status === "processed" ||
              flowMessage.status === "failed" ||
              flowMessage.status === "cancelled"
            ) {
              markMessageAsTerminal(flowMessage.id);
            }
          }

          // Handle the event with our global handler
          handleWebSocketEvent(data);

          // Notify all registered custom handlers
          handlersRef.current.forEach((handler) => handler(data));
        };

        socket.onclose = (ev) => {
          if (!isCurrentSocket()) return;
          debug(
            "EventStream WebSocket closed: code=%d reason=%s wasClean=%s",
            ev.code,
            ev.reason,
            ev.wasClean,
          );
          clearStreamingMessages();
          clearHeartbeatInterval();
          setIsConnected(false);

          // Server-initiated closes (e.g. 1001 going-away on deploy) and
          // abnormal closures (1006) are the signals we want to triage.
          // 1000 = normal, 1005 = no status (typical when FE called .close()).
          const expectedCodes = new Set([1000, 1005]);
          if (!expectedCodes.has(ev.code)) {
            Sentry.captureMessage("EventStream WebSocket closed unexpectedly", {
              level: "warning",
              tags: {
                ws_close_code: ev.code,
                ws_was_clean: ev.wasClean,
              },
              extra: {
                code: ev.code,
                reason: ev.reason,
                wasClean: ev.wasClean,
              },
            });
          }
        };

        socket.onerror = (event) => {
          if (!isCurrentSocket()) return;
          const readyState = socket.readyState;
          debug(
            "EventStream WebSocket error: readyState=%d event=%o",
            readyState,
            event,
          );
          Sentry.captureException(new Error("EventStream WebSocket error"), {
            tags: {
              ws_ready_state: readyState,
            },
            extra: {
              event,
              readyState,
            },
          });
        };
      } catch (error) {
        Sentry.captureException(
          new Error("Failed to connect to event stream"),
          {
            extra: {
              error,
            },
          },
        );
      }
    },
    1000,
    {
      leading: true,
    },
  );

  const registerHandler = (handler: EventStreamHandler) => {
    handlersRef.current.add(handler);

    return () => {
      handlersRef.current.delete(handler);
    };
  };

  // Clear any existing disconnect check interval
  const clearDisconnectCheckInterval = () => {
    if (disconnectCheckIntervalRef.current) {
      clearInterval(disconnectCheckIntervalRef.current);
      disconnectCheckIntervalRef.current = null;
    }
  };

  // Clear any existing heartbeat interval
  const clearHeartbeatInterval = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  };

  // Send an app-level ping on an interval and force a reconnect if the server
  // stops sending any frames (the half-open case the browser can't surface).
  const startHeartbeatInterval = () => {
    // Clear any existing interval first
    clearHeartbeatInterval();

    lastServerMessageAtRef.current = Date.now();

    heartbeatIntervalRef.current = setInterval(() => {
      const socket = socketRef.current;
      if (!socket || socket.readyState !== WebSocket.OPEN) {
        return;
      }

      const silenceMs = Date.now() - lastServerMessageAtRef.current;
      if (silenceMs > SERVER_SILENCE_TIMEOUT_MS) {
        debug(
          "EventStream WebSocket stale - no server message in %dms, reconnecting",
          silenceMs,
        );
        clearHeartbeatInterval();
        socket.close();
        socketRef.current = null;
        setIsConnected(false);
        connectWebSocket();
        return;
      }

      debug("EventStream WebSocket heartbeat ping");
      socket.send(JSON.stringify({ type: "ping" }));
    }, HEARTBEAT_INTERVAL_MS);
  };

  // Disconnect when window is hidden to save resources
  // Only disconnect if we're not currently receiving streaming messages
  const disconnectWebSocket = () => {
    if (socketRef.current && !isReceivingMessagesRef.current) {
      debug("EventStream WebSocket disconnecting due to window hidden");
      socketRef.current.close();
      socketRef.current = null;
      setIsConnected(false);
      clearDisconnectCheckInterval();
      // onclose now guards on isCurrentSocket and won't run after the ref is
      // nulled, so clear the heartbeat here instead of relying on it.
      clearHeartbeatInterval();
    } else if (isReceivingMessagesRef.current) {
      debug("EventStream WebSocket kept alive - currently receiving messages");
    }
  };

  // Start checking periodically if we can disconnect
  const startDisconnectCheckInterval = () => {
    // Clear any existing interval first
    clearDisconnectCheckInterval();

    debug(
      "Starting disconnect check interval - will disconnect once streaming completes",
    );

    disconnectCheckIntervalRef.current = setInterval(() => {
      if (
        !isReceivingMessagesRef.current &&
        document.visibilityState === "hidden"
      ) {
        debug("Streaming completed while hidden - disconnecting WebSocket");
        disconnectWebSocket();
      }
    }, 2000); // Check every 2 seconds
  };

  // Add reconnection logic and disconnect on visibility change
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      // Clear the disconnect check interval when window becomes visible
      clearDisconnectCheckInterval();

      // Reconnect if needed
      if (
        !socketRef.current ||
        socketRef.current.readyState !== WebSocket.OPEN
      ) {
        debug("Reconnecting EventStream WebSocket...");
        connectWebSocket();
      }
    } else if (document.visibilityState === "hidden") {
      // Try to disconnect immediately
      disconnectWebSocket();

      // If we couldn't disconnect because messages are streaming,
      // start an interval to check when streaming completes
      if (isReceivingMessagesRef.current && socketRef.current) {
        startDisconnectCheckInterval();
      }
    }
  };

  useEffect(() => {
    connectWebSocket();

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const reconnectInterval = setInterval(() => {
      // Only attempt reconnection if window is visible
      if (document.visibilityState === "visible") {
        if (
          !socketRef.current ||
          (socketRef.current.readyState !== WebSocket.OPEN &&
            socketRef.current.readyState !== WebSocket.CONNECTING)
        ) {
          debug("Attempting to reconnect EventStream WebSocket...");
          connectWebSocket();
        }
      }
    }, 5000); // Check every 5 seconds

    const tokenRefreshInterval = setInterval(
      () => {
        // Only refresh token if window is visible and socket is open
        if (
          document.visibilityState === "visible" &&
          socketRef.current?.readyState === WebSocket.OPEN
        ) {
          connectWebSocket();
        }
      },
      1000 * 60 * 30, // Refresh every 30 minutes
    );

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(reconnectInterval);
      clearInterval(tokenRefreshInterval);
      clearDisconnectCheckInterval();
      clearHeartbeatInterval();
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [eventStreamUrl]);

  return (
    <EventStreamContext.Provider value={{ registerHandler, isConnected }}>
      {children}
    </EventStreamContext.Provider>
  );
});

export function useEventStream() {
  const context = useContext(EventStreamContext);
  if (!context) {
    throw new Error(
      "useEventStream must be used within an EventStreamProvider",
    );
  }
  return context;
}
